import type { Decimal } from '../../math/num';
import { D } from '../../math/num';
import type { DamageType } from '../../graph/types';

/**
 * Parts — the loot that builds the Mind (THE_MIND_AND_PARTS.md). Defs supply
 * shape (ports, base stats); instances supply rolled affixes + level. Abstract
 * ids + display names.
 */

export const PART_CATEGORIES = ['attack', 'amplifier', 'tuner', 'source', 'utility', 'frame'] as const;
export type PartCategory = (typeof PART_CATEGORIES)[number];

/**
 * The tier ladder, player-facing names in comments: Nerfed / Mid / Lit /
 * Sweaty / Based / Unit / GOATED. Internal ids predate the meme rebrand and
 * stay stable for saves. The top two tiers NEVER drop: the Foundry forges
 * them (Based x3 -> Unit, Unit x3 -> GOATED).
 */
export const RARITIES = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'unit', 'goated'] as const;
export type Rarity = (typeof RARITIES)[number];

/** The highest tier that drops from enemies; everything above is forged. */
export const MAX_DROP_RARITY: Rarity = 'legendary';

export const RARITY_INDEX: Record<Rarity, number> = { common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 4, unit: 5, goated: 6 };

/** Rarity multiplies a part's PRIMARY effect (damage, power, boost, tune, utility).
 *  This is the headline reason a Based beats a Nerfed of the same def, on top
 *  of extra affixes and extra ports. Surfaced in every part UI. The forged
 *  tiers continue the same accelerating curve. */
export const RARITY_MULT: Record<Rarity, number> = { common: 1, uncommon: 1.15, rare: 1.32, epic: 1.55, legendary: 1.85, unit: 2.2, goated: 2.65 };

/** Combat behaviour of an attack module — drives targeting in the sim. */
export type AttackKind = 'melee' | 'ranged' | 'aoe';

/** Stats an affix can roll. Combat stats apply locally (to an attack/tuner);
 *  global stats (drop/scrap/component) apply across the whole loadout;
 *  weightRed reduces the carrying part's weight. */
export type AffixStat =
  | 'critChance'
  | 'critMult'
  | 'power'
  | 'cooldown'
  | 'dropRate'
  | 'scrapMult'
  | 'componentMult'
  | 'weightRed';

export const GLOBAL_AFFIX_STATS: AffixStat[] = ['dropRate', 'scrapMult', 'componentMult'];

export interface RolledAffix {
  stat: AffixStat;
  /** Decimal magnitude (serialized as string in saves). Semantics per stat. */
  value: Decimal;
}

export interface PartInstance {
  instanceId: string;
  defId: string;
  rarity: Rarity;
  level: number;
  affixes: RolledAffix[];
}

/** Accumulator of modifiers feeding an attack (Phase 2 of the evaluator). */
export interface ModBundle {
  powerAdd: Decimal; // fraction: 0.5 => +50%
  powerMult: Decimal; // product of independent xN
  critChanceAdd: number;
  critMultAdd: Decimal;
  cooldownRed: number; // fraction, clamped < 1
}

export function emptyMods(): ModBundle {
  return { powerAdd: D(0), powerMult: D(1), critChanceAdd: 0, critMultAdd: D(0), cooldownRed: 0 };
}

export interface GlobalMods {
  dropRateMult: Decimal;
  scrapMult: Decimal;
  componentMult: Decimal;
  /** Extra regen from equipped healing parts, as a fraction of max HP per second. */
  regenAdd: number;
  overloaded: boolean;
}

export function emptyGlobal(): GlobalMods {
  return { dropRateMult: D(1), scrapMult: D(1), componentMult: D(1), regenAdd: 0, overloaded: false };
}

/** Resolved attack ready for the combat sim. */
export interface EffectiveAttack {
  sourceNodeId: string;
  defId: string;
  name: string;
  kind: AttackKind;
  damageType: DamageType;
  damage: Decimal; // per hit
  cooldownMs: number;
  critChance: number; // 0..1
  critMult: Decimal;
}

export const RARITY_COLOR: Record<Rarity, string> = {
  common: '#9aa6b8',
  uncommon: '#5fd17a',
  rare: '#5aa9ff',
  epic: '#c98bff',
  legendary: '#ffb454',
  unit: '#ff6fae',
  goated: '#ffd24a',
};

export const RARITY_NAME: Record<Rarity, string> = {
  common: 'Nerfed',
  uncommon: 'Mid',
  rare: 'Lit',
  epic: 'Sweaty',
  legendary: 'Based',
  unit: 'Unit',
  goated: 'GOATED',
};
